import * as React from "react";
import { useEffect, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import styled from "styled-components";
import { useAuthentication, useHydration } from "@puregym/react-hooks";
import { ClientOnly } from "@puregym/utilities";
import { renewCookieConsent } from "@puregym/cookieconsent";
import {
  Button,
  BUTTON_VARIANTS,
  IconAccountCircle,
  IconCookieBite,
  IconMenu,
  Nav,
  NAV_VARIANTS,
  PageHeader,
  LOGO_HEIGHT,
} from "@puregym/ui";
import pureGym from "@puregym/ui/dist/static/images/logos/puregym/primary-inverted.svg";
import pureGymKsa from "@puregym/ui/dist/static/images/logos/puregymsa/primary-inverted.svg";
import pureGymUae from "@puregym/ui/dist/static/images/logos/puregymuae/primary-inverted.svg";
import pureFitness from "@puregym/ui/dist/static/images/logos/purefitness/primary-inverted.svg";

const getBrandLogo = (brand) => {
  switch (brand) {
    case "PGUK":
      return pureGym;
    case "PGSA":
      return pureGymKsa;
    case "PFUS":
      return pureFitness;
    case "UAE":
      return pureGymUae;
    default:
      throw new Error(`Unable to determine correct logo for brand '${brand}'`);
  }
};

const mapNavigationItems = (items) => {
  if (!items) {
    return;
  }

  return items.map(({ navigationItems, ...rest }) => ({
    ...rest,
    children: mapNavigationItems(navigationItems),
  }));
};

const UserMenuWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.halfSpacing};
`;

const Logo = styled.img`
  max-width: 100%;
  height: ${LOGO_HEIGHT}px;
`;

const Header = ({
  urls,
  brand,
  brandName,
  defaultLocale,
  siteUrl,
  enableCookieConsent = false,
  labels,
  ...headerProps
}) => {
  const { allNavigationItem } = useStaticQuery(graphql`
    {
      allNavigationItem {
        nodes {
          name
          to
          primary
          anonymous
          navigationItems {
            name
            to
            navigationItems {
              name
              to
            }
          }
        }
      }
    }
  `);

  const [anonymous, setAnonymous] = useState(true);
  const { isAuthenticated } = useAuthentication();
  const enabled = useHydration();

  // We need to trigger a rerender because the banner will be SSR'd
  // and React will ignore initial differences during hydration
  useEffect(() => {
    setAnonymous(!isAuthenticated);
  }, [isAuthenticated]);

  const mappedNavigationItems = mapNavigationItems(allNavigationItem.nodes);
  const currentLocale = process.env.GATSBY_LOCALE;

  const cookieConsent = {
    name: labels.cookieConsentModify,
    onClick: () => renewCookieConsent(currentLocale.substring(0, 2)),
    divider: true,
    icon: <IconCookieBite aria-hidden />,
  };

  const navigationItems = mappedNavigationItems.filter((navigationItem) =>
    anonymous ? navigationItem.anonymous : !navigationItem.anonymous
  );

  if (enableCookieConsent) {
    navigationItems.push(cookieConsent);
  }

  return (
    <PageHeader {...headerProps}>
      <Nav
        data-testid="globalNav"
        data={navigationItems}
        variant={NAV_VARIANTS.GLOBAL}
        width="320px"
        role="nav"
        labels={{
          back: labels.menuBackText,
          close: labels.closeMenuButton,
          title: labels.menuTitle,
        }}
        trigger={
          <>
            <IconMenu data-testid="menuIcon" title={labels.menuButton} />
            <span data-hidemobile aria-hidden>
              {labels.menuButton}
            </span>
          </>
        }
        buttonProps={{ disabled: !enabled }}
      />
      <div>
        <Button
          as="a"
          href={
            currentLocale === defaultLocale
              ? siteUrl
              : `${siteUrl}/${currentLocale.toLowerCase()}/`
          }
          data-testid="logo"
          variant={BUTTON_VARIANTS.PLAIN}
        >
          <Logo alt={brandName} src={getBrandLogo(brand)} />
        </Button>
      </div>
      <UserMenuWrapper>
        <ClientOnly fallback={<IconAccountCircle aria-hidden />}>
          {anonymous ? (
            <Button
              as="a"
              href={urls.authLogin}
              variant={BUTTON_VARIANTS.PLAIN}
            >
              <IconAccountCircle title={labels.logInButton} />
              <span data-hidemobile aria-hidden>
                {labels.logInButton}
              </span>
            </Button>
          ) : (
            <Button
              as="a"
              href={urls.authLogout}
              variant={BUTTON_VARIANTS.PLAIN}
            >
              <IconAccountCircle title={labels.logOutButton} />
              <span data-hidemobile aria-hidden>
                {labels.logOutButton}
              </span>
            </Button>
          )}
        </ClientOnly>
      </UserMenuWrapper>
    </PageHeader>
  );
};

export default Header;
